import { useState, useEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent } from '@/components/ui/card';
import { Separator } from '@/components/ui/separator';
import { Info, AlertTriangle, MessageCircle, Beaker, Clock, Pill, ArrowRightLeft, Loader2 } from 'lucide-react';
import { AdminBar } from '@/components/AdminBar';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ApplicationAreasPage() {
  const [areas, setAreas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openItems, setOpenItems] = useState([]);
  const location = useLocation();
  const navigate = useNavigate();
  const sectionRefs = useRef({});

  useEffect(() => {
    axios.get(`${API}/application-areas`)
      .then(r => {
        setAreas(r.data);
        setLoading(false);
      })
      .catch(() => setLoading(false));
  }, []);

  // Handle hash navigation
  useEffect(() => {
    if (areas.length === 0) return;
    const hash = location.hash.replace('#', '');
    if (hash) {
      setOpenItems([hash]);
      setTimeout(() => {
        sectionRefs.current[hash]?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 400);
    }
  }, [areas, location.hash]);

  const scrollToArea = (slug) => {
    if (!openItems.includes(slug)) {
      setOpenItems(prev => [...prev, slug]);
    }
    setTimeout(() => {
      sectionRefs.current[slug]?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 300);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20" data-testid="areas-loading">
        <Loader2 className="w-6 h-6 animate-spin text-[#12100c]" />
      </div>
    );
  }

  return (
    <div className="max-w-4xl" data-testid="application-areas-page">
      {/* Header */}
      <div className="mb-6">
        <h1 className="font-heading text-2xl sm:text-3xl font-bold text-[#12100c]" data-testid="areas-page-title">
          Aree di applicazione di UMIFUL
        </h1>
        <p className="text-sm text-[#4a4238] mt-2 leading-relaxed max-w-2xl">
          Guida pratica per il professionista: quando e come consigliare UMIFUL ai tuoi pazienti e clienti
        </p>
      </div>

      <AdminBar section="areas" onAdd={() => navigate('/admin', { state: { tab: 'areas', action: 'new' } })} />

      {/* Disclaimer */}
      <Card className="rounded-xl border-gray-200 bg-gray-50 mb-6" data-testid="areas-disclaimer">
        <CardContent className="p-4 flex gap-3">
          <Info className="w-5 h-5 text-[#4a4238] shrink-0 mt-0.5" strokeWidth={1.5} />
          <p className="text-xs text-[#4a4238] leading-relaxed">
            I contenuti di questa sezione sono destinati esclusivamente a professionisti del settore come supporto informativo.
            Non costituiscono indicazioni terapeutiche. I protocolli sono orientativi e vanno adattati alla situazione individuale del paziente/cliente.
          </p>
        </CardContent>
      </Card>

      {/* Filter Chips */}
      <div className="flex gap-2 overflow-x-auto pb-3 mb-6 scrollbar-hide" style={{ scrollbarWidth: 'none' }} data-testid="area-filter-chips">
        {areas.map(area => (
          <button
            key={area.slug}
            onClick={() => scrollToArea(area.slug)}
            className={`shrink-0 flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium transition-all border
              ${openItems.includes(area.slug)
                ? 'bg-[#12100c] text-white border-[#12100c]'
                : 'bg-white text-[#4a4238] border-gray-200 hover:border-[#5f7d52] hover:text-[#12100c]'
              }`}
            data-testid={`filter-chip-${area.slug}`}
          >
            <span className="text-sm">{area.icon}</span>
            <span className="whitespace-nowrap">{area.title}</span>
          </button>
        ))}
      </div>

      {/* Accordion Sections */}
      <Accordion type="multiple" value={openItems} onValueChange={setOpenItems} className="space-y-3">
        {areas.map(area => (
          <AccordionItem
            key={area.slug}
            value={area.slug}
            ref={el => { sectionRefs.current[area.slug] = el; }}
            className="border border-gray-100 rounded-2xl overflow-hidden bg-white shadow-sm"
            id={area.slug}
            data-testid={`area-section-${area.slug}`}
          >
            <AccordionTrigger className="px-5 py-4 hover:no-underline hover:bg-gray-50/50 [&[data-state=open]]:bg-[#12100c]/[0.02]">
              <div className="flex items-center gap-3 text-left">
                <span className="text-2xl shrink-0">{area.icon}</span>
                <div>
                  <h3 className="font-semibold text-[#12100c] text-base leading-tight">{area.title}</h3>
                  <div className="flex items-center gap-2 mt-1">
                    <Badge className={`text-[10px] border-none font-semibold ${
                      area.priority === 'alta'
                        ? 'bg-[#c6a15b]/15 text-[#8a6620]'
                        : 'bg-[#5f7d52]/10 text-[#4a5f43]'
                    }`}>
                      {area.priority === 'alta' ? 'Alta priorità' : 'Media priorità'}
                    </Badge>
                    <span className="text-xs text-[#4a4238]">{area.short_description}</span>
                  </div>
                </div>
              </div>
            </AccordionTrigger>

            <AccordionContent className="px-5 pb-6">
              <div className="space-y-5 pt-2">
                {/* Quando consigliarlo */}
                {area.when_to_use && area.when_to_use.length > 0 && (
                <AreaSection icon={<Pill className="w-4 h-4" />} title="Quando consigliarlo" color="text-[#12100c]">
                  <ul className="space-y-1.5">
                    {area.when_to_use.map((item, i) => (
                      <li key={i} className="flex items-start gap-2 text-sm text-[#4a4238]">
                        <span className="w-1.5 h-1.5 rounded-full bg-[#5f7d52] mt-1.5 shrink-0" />
                        {item}
                      </li>
                    ))}
                  </ul>
                </AreaSection>
                )}

                {area.mechanism && <>
                <Separator className="bg-gray-100" />
                {/* Perché funziona */}
                <AreaSection icon={<Beaker className="w-4 h-4" />} title="Perché funziona" color="text-[#4a5f43]">
                  <p className="text-sm text-[#4a4238] leading-relaxed">{area.mechanism}</p>
                </AreaSection>
                </>}

                {area.protocol && area.protocol.length > 0 && <>
                <Separator className="bg-gray-100" />
                {/* Protocollo consigliato */}
                <div className="bg-[#c6a15b]/8 rounded-xl p-4 border border-[#c6a15b]/15" data-testid={`protocol-${area.slug}`}>
                  <div className="flex items-center gap-2 mb-3">
                    <Clock className="w-4 h-4 text-[#8a6620]" strokeWidth={1.5} />
                    <h4 className="font-semibold text-sm text-[#12100c]">Protocollo consigliato</h4>
                  </div>
                  <ul className="space-y-1.5">
                    {area.protocol.map((item, i) => (
                      <li key={i} className="flex items-start gap-2 text-sm text-[#4a4238]">
                        <span className="text-[#8a6620] font-bold text-xs mt-0.5">{i + 1}.</span>
                        {item}
                      </li>
                    ))}
                  </ul>
                </div>
                </>}

                {/* Risultati attesi */}
                {area.expected_results && area.expected_results.length > 0 && (
                <AreaSection icon={<Clock className="w-4 h-4" />} title="Risultati attesi" color="text-[#12100c]">
                  <div className="space-y-2">
                    {area.expected_results.map((r, i) => {
                      const parts = (r || '').split(': ');
                      const timeline = parts[0] || '';
                      const result = parts.slice(1).join(': ') || r;
                      return (
                        <div key={i} className="flex items-start gap-3">
                          <span className="shrink-0 text-xs font-semibold text-[#4a5f43] bg-[#5f7d52]/10 px-2 py-0.5 rounded-full whitespace-nowrap mt-0.5">
                            {timeline}
                          </span>
                          <p className="text-sm text-[#4a4238]">{result}</p>
                        </div>
                      );
                    })}
                  </div>
                </AreaSection>
                )}

                {area.synergies && <>
                <Separator className="bg-gray-100" />
                {/* Sinergie */}
                <AreaSection icon={<ArrowRightLeft className="w-4 h-4" />} title="Sinergie" color="text-[#4a5f43]">
                  <p className="text-sm text-[#4a4238] leading-relaxed">{area.synergies}</p>
                </AreaSection>
                </>}

                {/* Frase pronta */}
                {area.ready_phrase && (
                <div className="border-l-4 border-[#5f7d52] bg-[#5f7d52]/5 rounded-r-xl p-4" data-testid={`ready-phrase-${area.slug}`}>
                  <div className="flex items-center gap-2 mb-2">
                    <MessageCircle className="w-4 h-4 text-[#4a5f43]" strokeWidth={1.5} />
                    <h4 className="font-semibold text-sm text-[#12100c]">Frase pronta per la consulenza</h4>
                  </div>
                  <p className="text-sm text-[#4a4238] leading-relaxed italic">
                    &ldquo;{area.ready_phrase}&rdquo;
                  </p>
                </div>
                )}

                {/* Note precauzionali */}
                {area.precautions && (
                  <div className="bg-amber-50 border border-amber-200 rounded-xl p-4" data-testid={`precautions-${area.slug}`}>
                    <div className="flex items-center gap-2 mb-2">
                      <AlertTriangle className="w-4 h-4 text-amber-600" strokeWidth={1.5} />
                      <h4 className="font-semibold text-sm text-amber-800">Note precauzionali</h4>
                    </div>
                    <p className="text-sm text-amber-700 leading-relaxed">{area.precautions}</p>
                  </div>
                )}
              </div>
            </AccordionContent>
          </AccordionItem>
        ))}
      </Accordion>
    </div>
  );
}

function AreaSection({ icon, title, color, children }) {
  return (
    <div>
      <div className="flex items-center gap-2 mb-2.5">
        <span className={color}>{icon}</span>
        <h4 className="font-semibold text-sm text-[#12100c]">{title}</h4>
      </div>
      {children}
    </div>
  );
}
